import React, { useEffect } from "react";
import { connect } from "react-redux";
import './CreateMailingInfo.css';
import MailingImageUploader from "./MailingImageUploader/MailingImageUploader";
import { useTranslation } from 'react-i18next';
import { ICreateMailingInfoProps } from "./~types/CreateMailingInfoProps";
import { IApplicationState } from 'src/~store/models/ApplicationState';
import { changeName, 
    changeMessage, 
    changeLinkText, 
    changeLinkUrl,
    changeIsEmergency,
    changeDisableLinkTracking,
    changeMailingType,
    changeSelectedBot,
    changeBotAdditionalData,
    changeSendingType,
    changeSelectedChannels,
    getEnabledChannels,
    fetchAndSetImageFromId
} from "src/app/mailing/~store/actions/actions";
import { channelsIdsSelector, enabledChannelsSelector, mailingItemInfoSelector } from "src/app/mailing/~store/selector";
import CreateMailingInfoPreview from "./CreateMailingInfoPreview/CreateMailingInfoPreview";

import Checkbox from '@mui/material/Checkbox';
import { MailingType } from "src/app/mailing/~store/models/enums/MailingType";
import { Input } from "reactstrap";
import { SendingType } from "./~types/enums/SendingType";
import OrderedChannelsSelector from "./OrderedChannelsSelector/OrderedChannelsSelector";
import ChannelsSelector from "./ChannelsSelector/ChannelsSelector";
import { ChannelType } from "src/app/channel/~store/models/enums/ChannelType";

const CreateMailingInfo = (props: ReturnType<typeof mapStateToProps> & ICreateMailingInfoProps) => {
    const { t } = useTranslation();

    useEffect(() => {
      props.getEnabledChannels();
    }, []);

    useEffect(() => {
        const isBotTabActive = props.mailingItemInfo.mailingType === MailingType.bot;
        const isBotSelected = !!props.mailingItemInfo.botStartParams?.sb;
        const hasBots = props.mailingItemInfo.allBots && props.mailingItemInfo.allBots.length > 0;

        if (isBotTabActive && !isBotSelected && hasBots) {
            props.changeSelectedBot(props.mailingItemInfo.allBots[0].name);
        }

    }, [
        props.mailingItemInfo.mailingType,
        props.mailingItemInfo.allBots,
        props.mailingItemInfo.botStartParams?.sb
    ]);

    useEffect(() => {
        const { mailingId, pictureId, image } = props.mailingItemInfo;

        if (mailingId !== 0 && pictureId && !image) {
            props.fetchAndSetImageFromId(pictureId);
        }

    }, [
        props.mailingItemInfo.mailingId,
        props.mailingItemInfo.pictureId,
        props.mailingItemInfo.image, 
        props.fetchAndSetImageFromId
    ]);

    const sendingTypeToString = (type: SendingType): string => {
        switch (type) {
            case SendingType.SendToLastMessenger:
                return t('mailing.sendToLastMessenger');
            case SendingType.SendToAllMessengers:
                return t('mailing.sendToAllMessengers');
            case SendingType.SendCascade:
                return t('mailing.sendCascade');
            default:
                return t('mailing.sendToLastMessenger');
        }
    }

    const stringToSendingType = (type: string): SendingType => {
        switch (type) {
            case t('mailing.sendToLastMessenger'):
                return SendingType.SendToLastMessenger;
            case t('mailing.sendToAllMessengers'):
                return SendingType.SendToAllMessengers;
            case t('mailing.sendCascade'):
                return SendingType.SendCascade;
            default:
                return SendingType.SendToLastMessenger;
        }
    }

    const _onNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.currentTarget.value;
        props.changeName(name);
    }

    const _onMessageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const message = e.currentTarget.value;
        props.changeMessage(message);
    }

    const _onLinkTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const buttonText = e.currentTarget.value;
        props.changeLinkText(buttonText);
    }

    const _onLinkUrlChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const buttonUrl  = e.currentTarget.value;
        props.changeLinkUrl(buttonUrl);
    }

    const _onIsEmergencyChanged = () => {
        props.changeIsEmergency(!props.mailingItemInfo.isEmergency);
    }

    const _onDisableLinkTrackingChanged = () => {
        props.changeDisableLinkTracking(!props.mailingItemInfo.disableLinkTracking);
    }

    const _onSendingTypeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const type = e.currentTarget.value;
        props.changeSendingType(stringToSendingType(type));
    }

    const _onBotAdditionalDataChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const data  = e.currentTarget.value;
        props.changeBotAdditionalData(data);
    }

    const getStartBotName = (): string => {
        return props.mailingItemInfo.botStartParams?.sb ?? "";
    }

    const _getAllBotsDropdown = () => {
        return (
            <Input
                type="select"
                className="mailing-item-info__bot-input"
                data-testid="mailing-bot"
                value={getStartBotName()}
                onChange={(event) => {props.changeSelectedBot(event.currentTarget.value)}} 
            >
                {props.mailingItemInfo.allBots.map(bot => {
                    return (<option>{bot.name}</option>)
                })}
            </Input>
        );
    }

    const _getSendingTypeDropdown = () => {
        return (
            <Input
                type="select"
                className="mailing-item-info__bot-input"
                data-testid="mailing-sending-type"
                value={sendingTypeToString(props.mailingItemInfo.sendingType)}
                onChange={_onSendingTypeChanged}
            >
                <option>{sendingTypeToString(SendingType.SendToLastMessenger)}</option>
                <option>{sendingTypeToString(SendingType.SendToAllMessengers)}</option>
                <option>{sendingTypeToString(SendingType.SendCascade)}</option>
            </Input>
        );
    }

    const _getSelectedChannelsOrderedInput = () => {
        return (
            <div className="channels-selector-wrapper">
                <OrderedChannelsSelector title={t("common.channels")} 
                    selectedChannelsIds = {props.channelsIds}
                    channels = { props.enabledChannels.filter(ch => ch.supportsMailing) } 
                    setSelectedChannelsIds = {props.changeSelectedChannels}/>
            </div>
        );
    }
    const _getSelectedChannelsInput = () => {
        return (
            <div className="channels-selector-wrapper">
                <ChannelsSelector title={t("common.channels")} 
                    selectedChannelsIds = {props.channelsIds}
                    channels = { props.enabledChannels.filter(ch => ch.supportsMailing) } 
                    setSelectedChannelsIds = {props.changeSelectedChannels}/>
            </div>
        );
    }

    const _getCreatingMessageMailingComponent = () => {
        return (
          <>
            <div className="mailing-item-info__text-block mailing-item-info__mailing-field">
              <span className="mailing-item-info__text-label">
                {t("common.message")}
              </span>
              <textarea
                className="mailing-item-info__text-input"
                data-testid="mailing-text"
                value={props.mailingItemInfo.text}
                onChange={_onMessageChanged}
              />
            </div>
            <div className="mailing-item-info__link-text-block mailing-item-info__mailing-field">
              <span className="mailing-item-info__text-label">
                {t("mailing.linkText")}
              </span>
              <input
                type="text"
                className="mailing-item-info__text-input"
                data-testid="mailing-link-text"
                value={props.mailingItemInfo.buttonText}
                onChange={_onLinkTextChanged}
              />
            </div>
            <div className="mailing-item-info__link-url-block mailing-item-info__mailing-field">
              <span className="mailing-item-info__text-label">
                {t("mailing.linkUrl")}
              </span>
              <input
                type="text"
                className="mailing-item-info__text-input"
                data-testid="mailing-link-url"
                value={props.mailingItemInfo.buttonUrl}
                onChange={_onLinkUrlChanged}
              />
            </div>
            <div className="mailing-item-info__text-block">
              <span className="mailing-item-info__text-label">{t("mailing.disableLinkTracking")}</span>
              <div className="mailing-params-checkbox-container mailing-params-checkbox">
                            <Checkbox
                                className="mailimg-params-check"
                                color="success"
                                checked={props.mailingItemInfo.disableLinkTracking}
                                onClick={_onDisableLinkTrackingChanged}
                                tabIndex={0}
                                onKeyUp={(e) => e.key === "Enter" && _onDisableLinkTrackingChanged()}
                            />
              </div>
            </div>
            <div className="mailing-item-info__link-url-block mailing-item-info__mailing-field">
              <span className="mailing-item-info__text-label">
                {t("mailing.image")}
              </span>
              <MailingImageUploader />
            </div>
            <div className="mailing-item-info__text-block mailing-item-info__sending-type-field">
              <span className="mailing-item-info__text-label">
                {t("mailing.sendingType")}
              </span>
              {_getSendingTypeDropdown()}
            </div> 
              {props.mailingItemInfo.sendingType === SendingType.SendToAllMessengers &&
                <div className="mailing-params-container mailing-item-info__sending-type-field">
                  {_getSelectedChannelsInput()}
                </div>}
              {props.mailingItemInfo.sendingType === SendingType.SendCascade &&
                <div className="mailing-params-container mailing-item-info__sending-type-field">
                  {_getSelectedChannelsOrderedInput()}
                </div>}
            <div className="mailing-item-info__text-block">
              <span className="mailing-item-info__text-label">{t("mailing.isEmergency")}</span>
              <div className="mailing-params-checkbox-container mailing-params-checkbox">
                            <Checkbox
                                className="mailimg-params-check"
                                color="success"
                                checked={props.mailingItemInfo.isEmergency}
                                onClick={_onIsEmergencyChanged}
                                tabIndex={0}
                                onKeyUp={(e) => e.key === "Enter" && _onIsEmergencyChanged()}
                            />
              </div>
            </div>
          </>
        );
    }

    const _getCreatingBotMailingComponent = () => {
        return  (
            <>
                <div className="mailing-item-info__text-block mailing-item-info__mailing-field">
                    <span className="mailing-item-info__bot-label">{t('mailing.bot')}</span>
                    {_getAllBotsDropdown()}
                </div>
                <div className="mailing-item-info__link-text-block mailing-item-info__mailing-field">
                    <span className="mailing-item-info__additional-data-label">{t('mailing.additionalData')}</span>
                    <input type="text" className="mailing-item-info__additional-data-input" value={props.mailingItemInfo.botStartParams?.a ?? ""} onChange={_onBotAdditionalDataChanged}/>
                </div>
            </>
        );
    }

    return (
        <div className="mailing-item-info__create-mailing">
            <div className="mailing-item-info__mailing-fields">
                <div className="mailing-item-info__name-block">
                    <div className="mailing-item-info__name-label">{t('common.name')}</div>
                    <input className="mailing-item-info__name-input" data-testid="mailing-name" type="text" value={props.mailingItemInfo.name} onChange={_onNameChanged}/>
                </div>                
                <div className="mailing-type-tabs-wrapper">
                    <span 
                        className={props.mailingItemInfo.mailingType !== MailingType.bot ? "active" : ""}
                        tabIndex={0}
                        onClick={() => props.changeMailingType(MailingType.message)}
                        onKeyUp={(e) => e.key === "Enter" && props.changeMailingType(MailingType.message)}
                    >
                        {t('mailing.message')}
                    </span>
                    <span 
                        className={props.mailingItemInfo.mailingType === MailingType.bot ? "active" : ""}
                        tabIndex={0}
                        onClick={() => {props.changeMailingType(MailingType.bot)}}
                        onKeyUp={(e) => e.key === "Enter" && props.changeMailingType(MailingType.bot)}
                    >
                        {t('mailing.bot')}
                    </span>
                </div>                
                {props.mailingItemInfo.mailingType !== MailingType.bot && 
                    _getCreatingMessageMailingComponent() ||
                    _getCreatingBotMailingComponent()}
            </div>
            {props.mailingItemInfo.mailingType !== MailingType.bot &&
                <CreateMailingInfoPreview
                    text={props.mailingItemInfo.text}
                    buttonText={props.mailingItemInfo.buttonText}
                    buttonUrl ={props.mailingItemInfo.buttonUrl }
                    image={props.mailingItemInfo.image}
                    pictureId={props.mailingItemInfo.pictureId} 
                />}
        </div>
    )
};

const mapStateToProps = (state: IApplicationState) => {
    return {
        mailingItemInfo: mailingItemInfoSelector(state),
        enabledChannels: enabledChannelsSelector(state),
        channelsIds: channelsIdsSelector(state),
    };
};

const mapDispatchToProps = (dispatch: any): ICreateMailingInfoProps => {
    return {
        changeBotAdditionalData: (data: string) => dispatch(changeBotAdditionalData(data)),
        changeSelectedBot: (selectedBot: string) => dispatch(changeSelectedBot(selectedBot)),
        changeMailingType: (mailingType: MailingType) => dispatch(changeMailingType(mailingType)),
        changeName: (name: string) => dispatch(changeName(name)),
        changeMessage: (message: string) => dispatch(changeMessage(message)),
        changeLinkText: (linkText: string) => dispatch(changeLinkText(linkText)),
        changeLinkUrl: (linkUrl: string) => dispatch(changeLinkUrl(linkUrl)),
        changeIsEmergency: (isEmergency: boolean) => dispatch(changeIsEmergency(isEmergency)),
        changeDisableLinkTracking: (disableLinkTracking: boolean) => dispatch(changeDisableLinkTracking(disableLinkTracking)),
        changeSendingType: (sendingType: SendingType) => dispatch(changeSendingType(sendingType)),
        changeSelectedChannels: (channelsIds: number[]) => dispatch(changeSelectedChannels(channelsIds)),
        getEnabledChannels: () => dispatch(getEnabledChannels()),
        fetchAndSetImageFromId: (pictureId: string) => dispatch(fetchAndSetImageFromId(pictureId))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateMailingInfo);

